import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes, useParams } from 'react-router-dom';
import AdminShell from './components/admin/AdminShell';
import AcceptInvite from './components/auth/AcceptInvite';
import Activate from './components/auth/Activate';
import ForgotPassword from './components/auth/ForgotPassword';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import ResetPassword from './components/auth/ResetPassword';
import BillingPage from './pages/admin/BillingPage';
import SelfHostedPage from './pages/admin/SelfHostedPage';
import SettingsPage from './pages/admin/SettingsPage';
import SiteDetailPage from './pages/admin/SiteDetailPage';
import SitesPage from './pages/admin/SitesPage';
import SupportPage from './pages/admin/SupportPage';
import TutorialsPage from './pages/admin/TutorialsPage';
import RequireStaff from './components/super-admin/RequireStaff';

// Ops console — staff-only, lazy-loaded so this code never enters the
// customer bundle. The console stylesheet is imported by the lazy chunk only.
const OpsOverview = lazy(() => import('./pages/super-admin/OverviewPage'));
const OpsAccounts = lazy(() => import('./pages/super-admin/AccountsPage'));
const OpsAccountDetail = lazy(() => import('./pages/super-admin/AccountDetailPage'));
const OpsSites = lazy(() => import('./pages/super-admin/SitesPage'));
const OpsLicenses = lazy(() => import('./pages/super-admin/LicensesPage'));
const OpsTrials = lazy(() => import('./pages/super-admin/TrialsPage'));
const OpsRevenue = lazy(() => import('./pages/super-admin/RevenuePage'));

function LegacySiteRedirect() {
  const { id } = useParams();
  return <Navigate to={`/sites/${id}`} replace />;
}

function App() {
  return (
    <Router>
      <Routes>
        <Route element={<AdminShell />}>
          <Route path="/" element={<Navigate to="/sites" replace />} />
          <Route path="/sites" element={<SitesPage />} />
          <Route path="/sites/:id" element={<SiteDetailPage />} />
          <Route path="/site/:id" element={<LegacySiteRedirect />} />
          <Route path="/billing" element={<BillingPage />} />
          <Route path="/self-hosted" element={<SelfHostedPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/tutorials" element={<TutorialsPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/learn" element={<Navigate to="/tutorials" replace />} />
          <Route path="/subscription" element={<Navigate to="/billing" replace />} />
          <Route path="/updates" element={<Navigate to="/sites" replace />} />
          <Route path="/project/create" element={<Navigate to="/sites" replace />} />
        </Route>

        {/* Staff-only Ops Console */}
        <Route element={<RequireStaff />}>
          <Route
            path="/ops/*"
            element={
              <Suspense fallback={<div style={{ padding: 40, fontFamily: 'monospace', color: '#888' }}>Loading console…</div>}>
                <Routes>
                  <Route path="/" element={<OpsOverview />} />
                  <Route path="accounts" element={<OpsAccounts />} />
                  <Route path="accounts/:id" element={<OpsAccountDetail />} />
                  <Route path="sites" element={<OpsSites />} />
                  <Route path="licenses" element={<OpsLicenses />} />
                  <Route path="trials" element={<OpsTrials />} />
                  <Route path="revenue" element={<OpsRevenue />} />
                  <Route path="*" element={<Navigate to="/ops" replace />} />
                </Routes>
              </Suspense>
            }
          />
        </Route>

        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />
        <Route path="/activate" element={<Activate />} />
      </Routes>
    </Router>
  );
}

export default App;
